import service from './http'
import serve from "@/utils/serve";

/**
 * A partir de quantos caracteres se busca.
 *
 * Escrito **duas vezes**, uma em cada repositório, como a escada de papéis: o `MIN_TEXT` do
 * `utils/instanceSearch.ts` do servidor é a garantia — ele recusa o texto curto venha de onde
 * vier —, e este é o desenho: é o que a tela diz antes de a pessoa terminar de digitar, e o que
 * a impede de pedir o que seria recusado.
 */
export const MIN_SEARCH_TEXT = 3

/**
 * O que o servidor achou, do jeito que a paleta desenha.
 *
 * **Sem o nome da coleção nem do campo**, só os aliases: os nomes são do catálogo do ambiente,
 * que a tela já tem em mão — e plural no uso, singular no design é decisão dela. Mandá-los do
 * servidor criaria um segundo dono para o mesmo rótulo.
 */
export type SearchHit = {
    /** O alias do campo que casou. */
    property: string
    /** O trecho em volta do casamento, já cortado, com reticências onde houve corte. */
    text: string
    /** Onde o casamento começa dentro do `text`, e quanto dura — é o que a tela realça. */
    at: number
    length: number
    /**
     * O valor cru, presente só quando o que está gravado NÃO é o que se mostra: o id da opção
     * numa seleção, o `'YYYY-MM-DD'` numa data. Nesses dois a tela desenha o VALOR com as peças
     * de sempre — o chip tingido, a data no formato de quem lê — e ignora o trecho, que serviu
     * para achar e não para exibir.
     */
    value?: number | string
}

export type SearchItem = {
    instance: number
    identifier?: string
    state?: number
    /**
     * Ausente é legítimo: o item pode ter vindo pelo próprio número, e aí não há campo a citar.
     * A linha então mostra só o identificador.
     */
    hit?: SearchHit
}

/** `count` é o total de acertos da coleção; `items` vem cortado. "Há mais" sai da diferença. */
export type SearchGroup = { idea: string, count: number, items: SearchItem[] }

export type SearchResult = { needle: string, groups: SearchGroup[] }

/**
 * A busca por texto em todos os itens do ambiente.
 *
 * Uma requisição para o ambiente inteiro, e não uma por coleção: é o servidor que atravessa as
 * coleções, numa consulta só. Pela tela, N requisições em paralelo trariam N coleções inteiras
 * para a memória do navegador a cada tecla.
 */
class SearchService {
    async find({systemAlias}: { systemAlias: string }, text: string): Promise<SearchResult> {
        return await serve(async () => await service.get(
            `systems/${systemAlias}/search`, {params: {q: text}}))
    }
}

export default SearchService
